import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, InputNumber, Select, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { HomeOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import '../pages/MemberManagement.css';

const { Paragraph, Text } = Typography;

type DiscountCategory = '耗材申请' | '试剂申请';

type DiscountRow = {
  key: string;
  teamId: string;
  category: DiscountCategory;
  brand: string;
  rate: number;
};

type MerchantTeam = {
  teamId: string;
  teamName: string;
  teamCode?: string;
};

type AvailableBrands = Record<DiscountCategory, string[]>;

const EMPTY_BRANDS: AvailableBrands = {
  耗材申请: ['代购下单'],
  试剂申请: []
};

async function merchantFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/merchant${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    }
  });
  return res.json();
}

const MerchantBrandDiscounts: React.FC = () => {
  const [rows, setRows] = useState<DiscountRow[]>([]);
  const [teams, setTeams] = useState<MerchantTeam[]>([]);
  const [availableBrands, setAvailableBrands] = useState<AvailableBrands>(EMPTY_BRANDS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await merchantFetch('/brand-discounts');
      if (!body.success) {
        message.error(body.error || '加载品牌折扣失败');
        return;
      }
      const discounts = (body.data?.discounts || []).map((row: Omit<DiscountRow, 'key'>, index: number) => ({
        ...row,
        teamId: String(row.teamId || ''),
        key: `${row.teamId}-${row.category}-${row.brand}-${index}`
      }));
      setRows(discounts);
      setTeams(body.data?.teams || []);
      setAvailableBrands({
        耗材申请: body.data?.availableBrands?.['耗材申请'] || EMPTY_BRANDS['耗材申请'],
        试剂申请: body.data?.availableBrands?.['试剂申请'] || []
      });
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateRow = (key: string, patch: Partial<DiscountRow>) => {
    setRows((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              ...patch,
              ...(patch.category || patch.teamId ? { brand: '' } : {})
            }
          : row
      )
    );
  };

  const save = async () => {
    if (rows.some((row) => !row.teamId || !row.brand || !Number.isFinite(row.rate))) {
      message.warning('请完整选择团队、品牌并填写折扣比例');
      return;
    }
    const combinations = new Set<string>();
    for (const row of rows) {
      const combination = `${row.teamId}\u0000${row.category}\u0000${row.brand}`;
      if (combinations.has(combination)) {
        message.error('同一入驻团队、商品类型和品牌只能设置一个折扣比例');
        return;
      }
      combinations.add(combination);
    }
    setSaving(true);
    try {
      const body = await merchantFetch('/brand-discounts', {
        method: 'PUT',
        body: JSON.stringify({
          discounts: rows.map(({ teamId, category, brand, rate }) => ({ teamId, category, brand, rate }))
        })
      });
      if (!body.success) {
        message.error(body.error || '保存失败');
        return;
      }
      message.success('品牌折扣已保存');
      void load();
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<DiscountRow> = [
    {
      title: '入驻团队',
      width: 220,
      render: (_, row) => (
        <Select
          showSearch
          optionFilterProp="label"
          value={row.teamId || undefined}
          placeholder="请选择团队"
          style={{ width: '100%' }}
          options={teams.map((team) => ({
            value: team.teamId,
            label: team.teamCode ? `${team.teamName}（${team.teamCode}）` : team.teamName
          }))}
          onChange={(teamId) => updateRow(row.key, { teamId })}
        />
      )
    },
    {
      title: '商品类型',
      width: 160,
      render: (_, row) => (
        <Select
          value={row.category}
          style={{ width: '100%' }}
          options={[
            { value: '耗材申请', label: '耗材' },
            { value: '试剂申请', label: '试剂' }
          ]}
          onChange={(category: DiscountCategory) => updateRow(row.key, { category })}
        />
      )
    },
    {
      title: '品牌',
      render: (_, row) => (
        <Select
          showSearch
          value={row.brand || undefined}
          placeholder="请选择品牌"
          style={{ width: '100%' }}
          options={(availableBrands[row.category] || []).map((brand) => ({
            value: brand,
            label: brand,
            disabled: rows.some(
              (other) =>
                other.key !== row.key &&
                other.teamId === row.teamId &&
                other.category === row.category &&
                other.brand === brand
            )
          }))}
          onChange={(brand) => updateRow(row.key, { brand })}
        />
      )
    },
    {
      title: '折扣比例',
      width: 180,
      render: (_, row) => (
        <InputNumber
          min={0.01}
          max={10}
          step={0.01}
          precision={2}
          value={row.rate}
          style={{ width: '100%' }}
          onChange={(rate) => updateRow(row.key, { rate: Number(rate || 0) })}
        />
      )
    },
    {
      title: '说明',
      width: 180,
      render: (_, row) => (
        <Text type="secondary">
          {row.rate < 1 ? `${Number((row.rate * 10).toFixed(2))} 折` : row.rate > 1 ? `加价 ${Math.round((row.rate - 1) * 100)}%` : '原价'}
        </Text>
      )
    },
    {
      title: '操作',
      width: 90,
      render: (_, row) => (
        <Button danger type="link" onClick={() => setRows((prev) => prev.filter((item) => item.key !== row.key))}>
          删除
        </Button>
      )
    }
  ];

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>品牌折扣</span>
      </nav>

      <header className="mm-page-head d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="mm-title">品牌折扣</h1>
          <p className="mm-subtitle mb-0">按耗材或试剂品牌设置价格乘数，0.85 表示八五折，1.2 表示加价 20%。</p>
        </div>
        <Space>
          <Button
            icon={<PlusOutlined />}
            disabled={!teams.length}
            onClick={() =>
              setRows((prev) => [
                ...prev,
                {
                  key: `new-${Date.now()}`,
                  teamId: teams[0]?.teamId || '',
                  category: '耗材申请',
                  brand: '',
                  rate: 1
                }
              ])
            }
          >
            添加品牌
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void save()}>
            保存
          </Button>
        </Space>
      </header>

      <div className="content-area px-1 px-md-2">
        <Card>
          <Paragraph type="secondary">
            每个入驻团队可独立配置；未配置的品牌按 1.00 计算。优惠后价格仅作为账单统计字段，不会覆盖订单原结算金额。
          </Paragraph>
          <Table rowKey="key" loading={loading} columns={columns} dataSource={rows} pagination={false} />
        </Card>
      </div>
    </div>
  );
};

export default MerchantBrandDiscounts;
